/**
 * Tandem — the visit pool: work owed but not yet in a diary, over a rolling
 * due-date horizon. Keyed on DUE date, not visit start, which is exactly why the
 * schedule cannot see this work; the two screens are complementary.
 *
 * It deliberately proposes no pairings. The spec is explicit that postcode area
 * was a stand-in and the real rule is drive distance, so half-day work is only
 * labelled pairable-on-capacity until routing exists.
 */
import { useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { Panel, SfLink } from "./ui";
import { useVisitPool, toISODate, addDays, useTeamFilter, useSalesforceLink } from "./data";
import type { SfVisit } from "./data";
import { scoreVisit, fmtPoints, VISIT_TYPE_LABEL, classify } from "./points";
import { usePointsConfig } from "./pointsConfig";
import BookVisitModal from "./BookVisitModal";
import type { BookTarget } from "./BookVisitModal";
import type { FilterOption } from "./TopBar";

/** Horizon presets. 90 days is the spec's pairing window, so it leads. ALL_TIME is
 * a wide range, not an unbounded query — the endpoint only takes a due-date range. */
const ALL_TIME = 3650;
const HORIZONS = [
    { days: 30, label: "30 days" },
    { days: 90, label: "90 days" },
    { days: 180, label: "6 months" },
    { days: ALL_TIME, label: "All time" },
];

/** Rows drawn. Filters run over the whole window first, so narrowing the search
 * or the consultant reaches work sitting past this cap. */
const SHOWN = 50;

const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86400000);

/** Unset priority sorts last. It is a Salesforce formula field, and 1 is the highest. */
const priorityOf = (v: SfVisit) => {
    const n = Number(v.priorityLevel);
    return Number.isFinite(n) ? n : 99;
};

const emptyActionSx = {
    border: `1px solid ${T.border}`,
    borderRadius: "8px",
    background: T.card,
    color: T.purpleDeep,
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 700,
    fontFamily: T.font,
    cursor: "pointer",
    "&:hover": { background: T.rowTint },
};

const Metric = ({ label, value, tip, color }: { label: string; value: string | number; tip?: string; color?: string }) => (
    <Box title={tip} sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "12px 14px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: color || T.purple, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.muted }}>
                {label}
            </Typography>
        </Box>
        <Typography sx={{ fontSize: "22px", fontWeight: 800, color: T.ink2, mt: "4px", fontVariantNumeric: "tabular-nums" }}>
            {value}
        </Typography>
    </Box>
);

// The Points column drops out with the flag, so the tracks follow it. Pri sits
// where the day planner puts it — after Points, before the consultant.
const gridFor = (withPoints: boolean) =>
    withPoints
        ? "92px 1.6fr 1.3fr 78px 46px 1fr 92px 74px 84px"
        : "92px 1.6fr 1.3fr 46px 1fr 92px 74px 84px";

const PoolScreen = ({
    team = "AllTeams",
    consultant = "AllConsultants",
    consultantOptions = [],
    onConsultant,
}: {
    team?: string;
    /** A consultant's email, or "AllConsultants". Set when the day planner sent the user here. */
    consultant?: string;
    consultantOptions?: FilterOption[];
    onConsultant?: (value: string) => void;
}) => {
    const { config, showPoints } = usePointsConfig();
    const inTeam = useTeamFilter(team);
    const sfLink = useSalesforceLink();
    const [horizon, setHorizon] = useState(90);
    const [includeOverdue, setIncludeOverdue] = useState(false);
    const [query, setQuery] = useState("");
    const [booking, setBooking] = useState<BookTarget | null>(null);
    const allTime = horizon === ALL_TIME;

    // Memoised: a fresh Date in the render body is a new identity every render,
    // which would bust the rows memo below and re-score the whole pool each time.
    const today = useMemo(() => new Date(), []);
    // Rolling window. Overdue work is real and owed, so it is pulled in — but a
    // 12-month floor, because the historic backlog is large.
    // Overdue is off by default: it is most of the pool and the slow part of the
    // fetch. All time spans it anyway, so the toggle is disabled under that preset.
    const from = allTime ? addDays(today, -ALL_TIME) : includeOverdue ? addDays(today, -365) : today;
    const to = addDays(today, horizon);
    const pool = useVisitPool(toISODate(from), toISODate(to));
    // placeholderData keeps the previous window mounted, so isLoading is false on
    // every change but the first. This is the flag that says "these rows are stale".
    const reloading = pool.isFetching && !pool.isLoading;
    const horizonLabel = HORIZONS.find((h) => h.days === horizon)?.label ?? `${horizon} days`;

    const rows = useMemo(() => {
        const all = pool.data ?? [];
        // A chosen consultant replaces the team filter rather than narrowing it, or
        // arriving from someone's planner could land on an empty page.
        const only = consultant === "AllConsultants" ? "" : consultant.toLowerCase();
        // Every word has to match somewhere, so "fire leeds" narrows rather than
        // widening. Matched against what the row actually shows.
        const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
        const matches = (v: SfVisit) => {
            if (words.length === 0) return true;
            const hay = [v.accountName, v.subActivity, v.name, v.allocatedToName, v.status]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return words.every((w) => hay.includes(w));
        };
        return all
            .filter((v) =>
                only ? (v.allocatedToEmail || "").toLowerCase() === only : inTeam(v.allocatedToEmail),
            )
            .filter(matches)
            .map((v) => {
                const score = scoreVisit(
                    {
                        subActivity: v.subActivity,
                        name: v.name,
                        visitCategory: v.visitCategory,
                        industry: v.industry,
                        fraRiskLevel: v.fraRiskLevel,
                        highRiskFra: v.highRiskFra,
                    },
                    config,
                    // Unbooked work has no On Site / Remote flag yet.
                    { assumeOnSiteIfUnset: true },
                );
                const due = v.dueDate ? new Date(v.dueDate) : null;
                // Days past the due date; 0 or less is still ahead of it.
                const overdueBy = due ? daysBetween(due, today) : 0;
                return { v, score, due, overdueBy };
            })
            // Due date first, priority as the tiebreak — the same order the day
            // planner uses, so the two screens read the same way.
            .sort(
                (a, b) =>
                    (a.v.dueDate || "").localeCompare(b.v.dueDate || "") ||
                    priorityOf(a.v) - priorityOf(b.v),
            );
    }, [pool.data, inTeam, config, today, consultant, query]);

    const totals = useMemo(() => {
        const points = rows.reduce((n, r) => n + (r.score.points ?? 0), 0);
        return {
            count: rows.length,
            points: Math.round(points * 10) / 10,
            // Half-day work is what pairing would consume. Counted, not paired.
            pairable: rows.filter((r) => r.score.points === config.pairableCapacity).length,
            unrated: rows.filter((r) => r.score.industryMissing).length,
            overdue: rows.filter((r) => r.overdueBy > 0).length,
        };
    }, [rows, config]);

    if (pool.isLoading)
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: "48px" }}>
                <CircularProgress size={26} />
            </Box>
        );
    if (pool.isError) return <Box sx={{ p: "28px", color: T.muted }}>Couldn't load the visit pool.</Box>;

    return (
        <Box sx={{ padding: "24px 28px 56px", display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* A filter, so it composes with the consultant and horizon rather than
                overriding them. */}
            <Box
                component="input"
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                placeholder="Search account, visit type, consultant or status…"
                sx={{
                    alignSelf: "flex-start",
                    width: "100%",
                    maxWidth: "380px",
                    border: `1px solid ${query ? T.purple : T.border}`,
                    borderRadius: "8px",
                    padding: "8px 12px",
                    fontSize: "13px",
                    fontFamily: T.font,
                    color: T.ink2,
                    background: T.card,
                    outline: "none",
                    "&:focus": { borderColor: T.purple },
                }}
            />

            {/* The top bar's own consultant box is hidden on this view, so there is
                only ever one of these. */}
            {onConsultant && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: T.muted }}>Consultant</Typography>
                    <Box
                        component="select"
                        value={consultant}
                        onChange={(e: any) => onConsultant(e.target.value)}
                        title="Narrows the pool to one person's allocated work. Leaving it on all consultants shows the whole team's."
                        sx={{
                            border: `1px solid ${consultant === "AllConsultants" ? T.border : T.purple}`,
                            borderRadius: "8px",
                            padding: "7px 10px",
                            fontFamily: T.font,
                            fontSize: "13px",
                            fontWeight: consultant === "AllConsultants" ? 400 : 700,
                            color: consultant === "AllConsultants" ? T.ink2 : T.purpleDeep,
                            background: T.card,
                            cursor: "pointer",
                            maxWidth: "230px",
                        }}
                    >
                        {consultantOptions.map((c) => (
                            <option key={c.value} value={c.value}>
                                {c.label}
                            </option>
                        ))}
                    </Box>
                    {consultant !== "AllConsultants" && (
                        <Typography sx={{ fontSize: "11.5px", color: T.muted2 }}>
                            Team filter does not apply while a consultant is chosen.
                        </Typography>
                    )}
                </Box>
            )}

            {/* Horizon — the pool is a moving window, so make moving it the first control */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: T.muted }}>Due within</Typography>
                <Box sx={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: "8px", overflow: "hidden" }}>
                    {HORIZONS.map((h) => (
                        <Box
                            key={h.days}
                            component="button"
                            onClick={() => setHorizon(h.days)}
                            sx={{
                                border: "none",
                                background: horizon === h.days ? T.purple : T.card,
                                color: horizon === h.days ? "#fff" : T.muted,
                                padding: "6px 14px",
                                fontSize: "12.5px",
                                fontWeight: 700,
                                cursor: "pointer",
                                fontFamily: T.font,
                            }}
                        >
                            {h.label}
                        </Box>
                    ))}
                </Box>
                <Box
                    component="button"
                    disabled={allTime}
                    onClick={() => setIncludeOverdue((o) => !o)}
                    title={
                        allTime
                            ? "All time already reaches back past every overdue visit."
                            : "Off by default — overdue work is most of the pool, and the slow part of the fetch. The floor is 12 months back."
                    }
                    sx={{
                        opacity: allTime ? 0.5 : 1,
                        cursor: allTime ? "default" : "pointer",
                        border: `1px solid ${includeOverdue || allTime ? T.red : T.border}`,
                        background: includeOverdue || allTime ? "#FBF1EF" : T.card,
                        color: includeOverdue || allTime ? T.red : T.muted,
                        borderRadius: "999px",
                        padding: "6px 13px",
                        fontSize: "12px",
                        fontWeight: 700,
                        fontFamily: T.font,
                    }}
                >
                    {includeOverdue || allTime ? "✓ " : ""}Include overdue
                </Box>
                {reloading && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                        <CircularProgress size={13} thickness={5} sx={{ color: T.purple }} />
                        <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: T.purpleDeep }}>
                            {`Loading ${horizonLabel}…`}
                        </Typography>
                    </Box>
                )}
                <Typography sx={{ fontSize: "11.5px", color: T.muted2, ml: "auto" }}>
                    {toISODate(from)} → {toISODate(to)}
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "12px",
                    opacity: reloading ? 0.45 : 1,
                    transition: "opacity .15s",
                }}
            >
                <Metric label="Visits owed" value={totals.count} tip="Work with a due date in this window and no diary slot" />
                {showPoints && (
                    <Metric label="Points owed" value={fmtPoints(totals.points)} tip="What this work would score once delivered" color={T.purpleDeep} />
                )}
                {/* Nothing can be overdue in a forward-only window, so the card would
                    sit at zero and read as broken. */}
                {(includeOverdue || allTime) && (
                    <Metric label="Overdue" value={totals.overdue} tip="Past its due date and still not booked" color={T.red} />
                )}
                {/* "Half-day work" is a points test — score.points === pairableCapacity —
                    so it goes with the rest of them, unlike the schedule's pairing flag
                    which is a clock-time rule and stands on its own. */}
                {showPoints && (
                    <Metric
                        label="Half-day work"
                        value={totals.pairable}
                        tip="Scores 1 point, so two could share a day — once drive times exist to say which two"
                        color={T.amber}
                    />
                )}
                {/* Counts accounts with no industry in Salesforce. A data-quality gap
                    that the scoring merely happens to notice, so it stays either way. */}
                <Metric
                    label="No industry"
                    value={totals.unrated}
                    tip={
                        showPoints
                            ? "Scored on the default; the account needs its industry set"
                            : "These accounts have no industry set in Salesforce and need updating"
                    }
                    color={T.amber}
                />
            </Box>

            {/* One list, soonest first. The Due column already says what each row is,
                and overdue rows are red. */}
            {rows.length > 0 && (
                    <Panel
                        title={`Owed · ${rows.length}`}
                        sub={
                            // A wider horizon appends later due dates below the cap, so the drawn
                            // rows can be identical before and after. This line is what moves.
                            (showPoints ? `${fmtPoints(totals.points)} points of work · ` : "") +
                            (rows.length > SHOWN
                                ? `showing the ${SHOWN} soonest — a wider horizon adds work past this cap`
                                : `all ${rows.length} shown, soonest due first`)
                        }
                    >
                        <Box sx={{ overflowX: "auto", opacity: reloading ? 0.45 : 1, transition: "opacity .15s" }}>
                            <Box sx={{ minWidth: "946px" }}>
                                <Box sx={{ display: "grid", gridTemplateColumns: gridFor(showPoints), background: T.rowTint, borderRadius: "8px", padding: "7px 10px" }}>
                                    {["Due", "Account", "Visit type", ...(showPoints ? ["Points"] : []), "Pri", "Consultant", "Status", "", ""].map((h, i) => (
                                        <Typography key={i} sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.muted }}>
                                            {h}
                                        </Typography>
                                    ))}
                                </Box>
                                {rows.slice(0, SHOWN).map(({ v, score, due, overdueBy }) => (
                                    <Box key={v.id} sx={{ display: "grid", gridTemplateColumns: gridFor(showPoints), alignItems: "center", padding: "7px 10px", borderTop: `1px solid ${T.borderSoft}` }}>
                                        <Typography
                                            title={overdueBy > 0 ? `${overdueBy} days overdue` : undefined}
                                            sx={{ fontSize: "12px", fontWeight: overdueBy > 0 ? 800 : 600, color: overdueBy > 0 ? T.red : T.ink2 }}
                                        >
                                            {due ? due.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "12.5px", color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "8px" }}>
                                            {v.accountName || "—"}
                                            {(v.latitude == null || v.longitude == null) && (
                                                <Box
                                                    component="span"
                                                    title="No coordinates on this account in Salesforce, so it cannot be mapped or distance-checked when planning a day."
                                                    sx={{ color: T.amber, fontWeight: 800, ml: "5px", cursor: "help" }}
                                                >
                                                    ⚠
                                                </Box>
                                            )}
                                        </Typography>
                                        <Typography sx={{ fontSize: "12px", color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "8px" }}>
                                            {v.subActivity || VISIT_TYPE_LABEL[classify(v.name)]}
                                        </Typography>
                                        {showPoints && (
                                            <Typography
                                                title={score.points == null ? score.rule : `${score.rule}${v.industry ? ` · ${v.industry}` : ""}`}
                                                sx={{ fontSize: "12.5px", fontWeight: 800, color: score.industryMissing ? T.amber : T.purpleDeep, fontVariantNumeric: "tabular-nums" }}
                                            >
                                                {score.points == null ? "—" : fmtPoints(score.points)}
                                            </Typography>
                                        )}
                                        <Typography
                                            title={
                                                v.priorityLevel == null || v.priorityLevel === ""
                                                    ? "No priority level set on this visit in Salesforce"
                                                    : `Priority level ${v.priorityLevel}`
                                            }
                                            sx={{
                                                fontSize: "12px",
                                                fontWeight: 800,
                                                fontVariantNumeric: "tabular-nums",
                                                color: v.priorityLevel == null || v.priorityLevel === "" ? T.muted2 : T.ink2,
                                            }}
                                        >
                                            {v.priorityLevel == null || v.priorityLevel === "" ? "—" : v.priorityLevel}
                                        </Typography>
                                        <Typography sx={{ fontSize: "12px", color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "8px" }}>
                                            {v.allocatedToName || "unallocated"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "11px", fontWeight: 700, pr: "12px", color: v.status === "To be booked" ? T.muted : T.amber }}>
                                            {v.status}
                                        </Typography>
                                        <Box
                                            component="button"
                                            disabled={!v.allocatedToId}
                                            onClick={() => v.allocatedToId && setBooking({ visit: v })}
                                            title={
                                                v.allocatedToId
                                                    ? `Book ${v.accountName || "this visit"}`
                                                    : "No consultant is allocated, so this cannot be booked yet"
                                            }
                                            sx={{
                                                border: `1px solid ${T.border}`,
                                                borderRadius: "7px",
                                                background: T.card,
                                                color: T.purpleDeep,
                                                padding: "4px 9px",
                                                fontSize: "11px",
                                                fontWeight: 800,
                                                fontFamily: T.font,
                                                cursor: v.allocatedToId ? "pointer" : "not-allowed",
                                                opacity: v.allocatedToId ? 1 : 0.45,
                                                "&:hover": { background: v.allocatedToId ? T.purpleTint : T.card },
                                            }}
                                        >
                                            Book
                                        </Box>
                                        <SfLink href={sfLink("Visit__c", v.id)} label={`Open ${v.accountName || "this visit"} in Salesforce`} />
                                    </Box>
                                ))}
                                {rows.length > SHOWN && (
                                    <Typography sx={{ fontSize: "11.5px", color: T.muted2, padding: "8px 10px" }}>
                                        Showing the {SHOWN} soonest of {rows.length}. Search, or narrow the consultant or
                                        horizon, to reach the rest — those filters run over the whole window, not just the
                                        rows drawn here.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Panel>
            )}

            {/* An empty pool almost never means "no work" — it means the window is
                narrow. Say so, and offer the two controls that widen it. */}
            {rows.length === 0 && (
                <Box sx={{ p: "28px", display: "flex", flexDirection: "column", gap: "10px", alignItems: "flex-start" }}>
                    <Typography sx={{ color: T.muted, fontSize: "13px" }}>
                        {query
                            ? `Nothing owed in this window matches "${query}".`
                            : consultant === "AllConsultants"
                              ? "Nothing owed in this window for the current filters."
                              : `Nothing owed for ${
                                    consultantOptions.find((c) => c.value === consultant)?.label || consultant
                                } between ${toISODate(from)} and ${toISODate(to)}.`}
                    </Typography>
                    {!query && (!includeOverdue || !allTime) && (
                        <Typography sx={{ color: T.muted2, fontSize: "12px" }}>
                            That is only this window. Most owed work is overdue or further out.
                        </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {!includeOverdue && !allTime && (
                            <Box component="button" onClick={() => setIncludeOverdue(true)} sx={emptyActionSx}>
                                Include overdue
                            </Box>
                        )}
                        {!allTime && (
                            <Box component="button" onClick={() => setHorizon(ALL_TIME)} sx={emptyActionSx}>
                                Search all dates
                            </Box>
                        )}
                        {query && (
                            <Box component="button" onClick={() => setQuery("")} sx={emptyActionSx}>
                                Clear the search
                            </Box>
                        )}
                        {consultant !== "AllConsultants" && onConsultant && (
                            <Box component="button" onClick={() => onConsultant("AllConsultants")} sx={emptyActionSx}>
                                Show every consultant
                            </Box>
                        )}
                    </Box>
                    {consultant !== "AllConsultants" && allTime && includeOverdue && (
                        <Typography sx={{ color: T.amber, fontSize: "12px" }}>
                            Nothing at any date. If Salesforce shows them allocated work, it has no due date — the pool
                            is keyed on due date, so that work cannot appear here. Worth raising with ops.
                        </Typography>
                    )}
                </Box>
            )}

            <Typography sx={{ fontSize: "11px", color: T.muted2, lineHeight: 1.6 }}>
                Work with a due date and no diary slot — statuses <b>To be booked</b>, <b>1</b>/<b>2 Attempts</b>,{" "}
                <b>Banked</b> and <b>Suspended</b>. Closed, cancelled and completed visits are excluded, since they
                are not owed.
                <br />
                Points are what the work <b>would</b> score. The On Site / Remote flag is only set once a visit is
                arranged, so most of the pool has none and is priced as an on-site visit — a remote one would score{" "}
                {fmtPoints(config.remotePoints)} instead.
                <br />
                <b>No pairings are proposed.</b> Deciding that two half-day jobs can share a day needs the drive
                time between them, and the specification is explicit that postcode area was only a stand-in for the
                analysis. Half-day work is counted above so the size of the opportunity is visible, but which two
                visits pair waits for real routing.
            </Typography>

            <BookVisitModal target={booking} onClose={() => setBooking(null)} />
        </Box>
    );
};

export default PoolScreen;
